'use client';

import type { ReactNode } from 'react';
import { CheckmarkCircle02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  TerminalAutoApprovalSchema,
  type TerminalAutoApproval,
} from '@terra/shared-dto-chat-types';
import { Badge } from '../../../../../components/badge';
import { Tooltip } from '../../../../../components/tooltip';

export type AutoApprovalSubject = 'command' | 'action';

export function readAutoApproval(
  value: unknown,
): TerminalAutoApproval | undefined {
  const parsed = TerminalAutoApprovalSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

export function AutoApprovalBadge({
  autoApproval,
  subject,
}: {
  autoApproval: TerminalAutoApproval;
  subject: AutoApprovalSubject;
}): ReactNode {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const title =
    autoApproval.source === 'rule'
      ? t('autoApprovedByRuleTooltip', {
          pattern: autoApproval.pattern ?? '',
        })
      : autoApproval.source === 'auto-review'
        ? subject === 'action'
          ? t('autoApprovedByActionReviewTooltip')
          : t('autoApprovedByReviewTooltip')
        : t('autoApprovedByPolicyTooltip');

  return (
    <Tooltip title={title} subtitle={autoApproval.reason} maxW="320px">
      <Badge
        variant="outline"
        size="xs"
        icon={<CheckmarkCircle02Icon size={12} />}
        color="fg.decorative.blue"
        borderColor="border.decorative.blue"
      >
        {t('autoApprovedBadge')}
      </Badge>
    </Tooltip>
  );
}
